/**
 * Material-UI style hooks and modal utilities for the CreateLevels component.
 *
 * @module components/sim/createLevels/CreateLevels
 * @author Covintus
 * @author Snowfly
 * @version 1.0.0
 */

import { makeStyles } from '@material-ui/core/styles';
import background from '../../../assets/images/create-levels/levels_bg_1.png';

/**
 * Custom hook for generating CSS-in-JS styles for a toolbar component using Material-UI's `makeStyles`.
 * Provides styles for various*/
export const useToolbarStyles = makeStyles((theme) => ({
    root: {
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2),
    },
    errorAlert: {
        backgroundColor: '#600727',
    },
    cancelButton: {
        color: '#313387',
    },
    title: {
        flexGrow: 1,
        fontWeight: 'bold',
    },
    icon: {
        padding: '6px',
        backgroundColor: '#F5E2E9',
        borderRadius: '11%',
        margin: theme.spacing(0, 1),
    },
    printIcon: {
        padding: '6px',
        backgroundColor: '#F5E2E9',
        borderRadius: '11%',
        margin: theme.spacing(0, 1),
        marginRight: '1.5rem',
    },
    nextBtn: {
        padding: '8px 40px 8px 40px',
    },
    previewBtn: {
        padding: '8px 40px 8px 40px',
        marginRight: 12,
    },
    divider: {
        marginTop: '5px',
    },
    previousBtn: {
        margin: '0rem 1rem',
        width: '35px',
        minWidth: 0,
        minHeight: 0,
        border: '2px solid rgb(136,38,71)',
        '&:hover': { border: '2px solid rgb(136,38,71)' },
        padding: '4px 0',
    },
    templateLink: {
        display: 'block',
        cursor: 'pointer',
        textAlign: 'end',
    },
    uploadDialog: {
        padding: '16px 24px 10px',
    },
}));

/**
 * Styles configuration for various UI components related to levels.
 * This object is generated using the `makeStyles` utility from Material-UI.
 *
 * Properties:
 * - `levelHeader`: Styles for the header of a level, setting the background color,
 *                  margins, width, and border radius.
 * - `errorAlert`: Styling for error alert backgrounds.
 * - `cancelButton`: Styling for the cancel button, including text color.
 * - `levels`: Styles for the container displaying levels, including dimensions,
 *             background color, text weight, padding, and border radius.
 * - `addLevel`: Styling for the "Add Level" option,
 *               including font size, positioning, and cursor behavior.
 * - `activeCard`: Styles for an active card, including padding, colors,
 *                 background, margins, and border radius.
 * - `inactiveCard`: Styles for an inactive card, similar to `activeCard`,
 *                   but with a different background.
 * - `checkCircleIcon`: Style rules for the "check circle" icon, including color,
 *                      size, and positioning adjustments.
 * - `label`: Styling for generic labels, dictating padding, font size,
 *            and font weight.
 * - `levelLabel`: Style properties for level-specific labels, with larger
 *                 font size and weight.
 * - `levelName`: Styles specific to the level naming section, including padding
 *                and text formatting.
 * - `simGeneratedLabel`: Styling for labels generated by simulations,
 *                        featuring right alignment and smaller text size.
 * - `rightArrow`: Styles for a right-arrow element, including dimensions,
 *                 positioning, and border customizations.
 * - `refreshIcon`: Styling for the refresh icon, including dimensions,
 *                  coloring, positioning, and padding.
 * - `firstGrid`: Styling for the first grid container, featuring padding.
 * - `text`: Styling for text input elements within the UI, targeting child
 *           `MuiInputBase-input` for color, font size, and weight.
 * - `textFielDiv`: Styling for the container holding text inputs, with padding.
 * - `saveBtn`: Style configuration for save buttons, including alignment,
 *              margin, padding, and minimal width.
 * - `cancelBtn`: Styling for cancel buttons, similarly formatted to `saveBtn`.
 * - `passfailIcon`: Styling for the "pass/fail" icon, highlighting its color
 *                   and alignment.
 */
export const useLevelsStyle = makeStyles((theme) => ({
    levelHeader: {
        background: '#2A416C',
        margin: '0',
        width: '100%',
        color: '#ffff',
        borderTopRightRadius: 4,
        borderTopLeftRadius: 4,
    },
    errorAlert: {
        backgroundColor: '#600727',
    },
    cancelButton: {
        color: '#313387',
    },
    levels: {
        margin: 'auto',
        width: '5vw',
        backgroundColor: '#3E5785',
        fontWeight: '800',
        padding: '0',
        borderBottomRightRadius: '1%',
        borderBottomLeftRadius: '1%',
    },
    addLevel: {
        fontSize: 12,
        marginTop: 100,
        marginBottom: 60,
        cursor: 'pointer',
        color: '#ffff',
    },
    activeCard: {
        padding: theme.spacing(1),
        color: 'black',
        background: '#ffff',
        margin: '7px',
        borderRadius: 3,
    },
    inactiveCard: {
        padding: theme.spacing(1),
        color: '#ffff',
        background: '#1B2D4F',
        margin: '7px',
        borderRadius: 3,
    },
    checkCircleIcon: {
        color: '#2CB174',
        height: 15,
        marginBottom: -5,
    },
    label: {
        padding: 8,
        fontSize: 10,
        fontWeight: '100',
    },
    levelLabel: {
        padding: 10,
        fontSize: 14,
        fontWeight: '600',
    },
    levelName: {
        padding: '0 10px 10px 10px',
        fontSize: 14,
        fontWeight: '600',
    },
    simGeneratedLabel: {
        float: 'right',
        fontSize: 8,
    },
    rightArrow: {
        width: '10px',
        height: '10px',
        borderTop: '10px solid transparent',
        borderBottom: '10px solid transparent',
        borderLeft: '10px solid white',
        position: 'absolute',
        top: '340px',
        right: '1168px',
        zIndex: '1000',
    },
    refreshIcon: {
        width: 20,
        height: 19,
        padding: 2,
        background: '#E9F0FE',
        color: '#3C5582',
        float: 'right',
        marginTop: -9,
        marginRight: -3,
    },
    firstGrid: {
        padding: 20,
    },
    text: {
        '& .MuiInputBase-input': {
            color: '#fff',
            fontSize: 14,
            fontWeight: '600',
        },
    },
    textFielDiv: {
        padding: 10,
    },
    saveBtn: {
        alignSelf: 'flex-end',
        marginBottom: -10,
        padding: 2,
        marginRight: 8,
        minWidth: '50px',
    },
    cancelBtn: {
        alignSelf: 'flex-end',
        marginBottom: -10,
        padding: 2,
        minWidth: '50px',
    },
    passfailIcon: {
        float: 'right !important',
        color: '#2CB174',
    },
}));

/**
 * useStyle is a variable initialized with a collection of custom styles defined using the `makeStyles` function from Material-UI.
 * These styles are created to be used across components for consistent theming, layout, and visual customization.
 * The `theme` object allows for responsive design with Material-UI's breakpoints and theme spacing utilities.
 *
 * Properties:
 * - toolBar: Styles for toolbar layout, including padding and justification.
 * - errorAlert: Custom background color for error alerts.
 * - cancelButton: Styling for a cancel button, including text color.
 * - scriptDiv: Configure size, scrolling behavior, and height limits for a script container.
 * - scriptDiv1: Alternate configuration for script container with different dimensions.
 * - mainDiv: Styles for a main div element with relative positioning.
 * - loaderDiv: Absolute positioning for a loader div, centered in its container.
 * - gridContainer: Margins and padding for grid containers.
 * - intialCharacterMainGrid: Styling for the initial character grid, including background, dimensions, and border.
 * - finalCharacterMainGrid: Styling for the final character grid with adjustments to the border.
 * - characterLabel: Typography styling for character labels.
 * - editCanvas: Styles for an editable canvas, including responsiveness and maximum width.
 * - canvasLabel: Typography styling for canvas labels with line-breaking behavior.
 * - editBtn: Padding styling for edit buttons.
 * - editIcon: Styles for icons used in edit buttons, including color, size, and shadowing.
 * - generateSimBtn: Styling for a simulation generation button with focus on font and display.
 * - iconBox: Container styling for icons with margin and whitespace adjustments.
 * - iconStar: Styles for a star icon including dimensions and cursor pointer behavior.
 * - iconFeedback: Layout and styling for feedback icons, with responsive adjustments.
 * - iconVolume: Visual and interactive styling for volume icons.
 * - iconCopy: Configuration for copy icons, including size and positioning.
 * - iconDownload: Styling for download icons with cursor interaction.
 * - iconDelete: Configuration for delete icons with size and alignment adjustments.
 * - verticalDivider: Styling for vertical dividers used as separators.
 * - verticalDiv: Variant styling for vertical div containers.
 * - backgroundImage: Background image configuration, including size, repeat, and container dimensions.
 * - dialogFormControl: Margins and padding for dialog form controls.
 * - resize: Typography styling for resizable text elements.
 * - generateButton: Styles for buttons, including usability settings.
 * - alert: Margins applied to alert containers.
 * - textfield: Adjustments for input fields, including dimensions and encapsulated element styles.
 * - checkbox: Layout and padding customization for checkboxes.
 * - modalPaper: Styling for modal dialogs, including padding, shadows, and positioning.
 * - progress: Spacing configuration for progress indicators.
 * - otherMicIcon: Styles for microphone icons used for "other" category, with responsive font sizing.
 * - agentMicIcon: Styles for agent microphone icons, including size adjustments.
 * - customerMicIcon: Custom styling for customer microphone icons with responsive behavior.
 * - coachMicIcon: Configuration for coach microphone icons and responsive adaptations.
 * - passFailDiv: Typography styling for pass/fail text elements.
 * - iconImport: Styling for import icons with interactive cursor behavior.
 */
export const useStyle = makeStyles((theme) => ({
    toolBar: {
        paddingLeft: 0,
        paddingRight: 0,
        justifyContent: 'space-between',
    },
    errorAlert: {
        backgroundColor: '#600727',
    },
    cancelButton: {
        color: '#313387',
    },
    scriptDiv: {
        width: '100%',
        height: '100%',
        overflowY: 'scroll',
        maxHeight: "calc(100vh - 26rem)",
    },
    scriptDiv1: {
        width: '103%',
        height: '88%',
        maxHeight: "calc(100vh - 26rem)",
    },
    mainDiv: {
        position: 'relative',
    },
    loaderDiv: {
        position: 'absolute',
        top: '40%',
        right: '50%',
    },
    gridContainer: {
        margin: theme.spacing(0, 0, 1, 0),
        paddingBottom: theme.spacing(8),
    },
    intialCharacterMainGrid: {
        margin: '11px 10px 20px 10px',
        background: '#F5F5F5',
        width: '16vw',
        borderRadius: '1%',
        padding: theme.spacing(1.5),
        [theme.breakpoints.down(1285)]: {
            width: '15vw',
            padding: 6,
        },
        border: '1px dotted',
    },
    finalCharacterMainGrid: {
        margin: '11px 10px 20px 10px',
        background: '#F5F5F5',
        width: '16vw',
        borderRadius: '1%',
        padding: theme.spacing(1.5),
        [theme.breakpoints.down(1285)]: {
            width: '15vw',
            padding: 6,
        },
        border: 'none',
    },
    characterLabel: {
        fontSize: 17,
        fontWeight: 700,
        marginTop: 12,
        marginLeft: 15,
    },
    editCanvas: {
        background: '#F5F5F5',
        borderRadius: '1%',
        marginTop: 11,
        marginBottom: 21,
        padding: theme.spacing(1.5),
        [theme.breakpoints.down(1285)]: {
            width: '63.3%',
        },
        maxWidth: '64%',
    },
    canvasLabel: {
        fontSize: 20,
        fontWeight: '800',
        flexGrow: 1,
        whiteSpace: 'break-spaces',
    },
    editBtn: {
        padding: '0px 8px 6px 8px',
    },
    editIcon: {
        marginLeft: -2,
        height: 23,
        width: 23,
        background: 'white',
        padding: 4,
        borderRadius: 3,
        color: '#3E5785',
        boxShadow: '0px 2px 0px #EEEEEE',
    },
    generateSimBtn: {
        color: '#475D84',
        background: 'white',
        border: '1px solid black',
        display: 'flex',
        fontSize: 11,
        fontWeight: 600,
        marginRight: 14,
    },
    iconBox: {
        background: '#ffff',
        margin: 10,
        whiteSpace: 'nowrap',
    },
    iconStar: {
        width: 40,
        height: 27,
        marginBottom: -2,
        color: '#3E5785',
        cursor: 'pointer',
    },
    iconFeedback: {
        color: '#3E5785 !important',
        marginRight: 8,
        marginLeft: 3,
        padding: 0,
        marginTop: -17,
        '& .MuiSvgIcon-root': {
            width: 19,
        },
    },
    iconVolume: {
        width: 29,
        height: 23,
        color: '#3E5785',
        cursor: 'pointer',
    },
    iconCopy: {
        width: 18,
        height: 25,
        marginLeft: 9,
        color: '#3E5785',
        cursor: 'pointer',
        marginBottom: -2,
    },
    iconDownload: {
        width: 40,
        height: 22,
        marginLeft: -2,
        color: '#3E5785',
        cursor: 'pointer',
    },
    iconDelete: {
        width: 40,
        height: 22,
        marginLeft: -7,
        color: '#3E5785',
        cursor: 'pointer',
    },
    verticalDivider: {
        marginRight: 5,
        verticalAlign: 'super',
        borderLeft: '2px solid #ededed9e',
        height: 10,
    },
    verticalDiv: {
        marginRight: 5,
        verticalAlign: 'super',
        borderLeft: '2px solid #ededed9e',
        height: 10,
        margin: 15,
    },
    backgroundImage: {
        backgroundImage: `url(${background})`,
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        width: '100%',
        height: '100%',
    },
    dialogFormControl: {
        margin: '0em 2em 2em 2em',
        padding: '0em 3em',
    },
    resize: {
        fontWeight: [600],
        fontSize: '1.1em',
    },
    generateButton: {
        marginRight: '1rem',
        pointerEvents: 'unset !important',
    },
    alert: {
        margin: 10,
    },
    textfield: {
        margin: theme.spacing(0, 0, 0, 1),
        '& .MuiOutlinedInput-root': {
            height: '29px',
            width: '69px',
        },
    },
    checkbox: {
        marginLeft: '1rem',
        padding: '0px',
        paddingBottom: '2px',
    },
    modalPaper: {
        position: 'absolute',
        width: 400,
        backgroundColor: theme.palette.background.paper,
        border: '2px solid #000',
        boxShadow: theme.shadows[5],
        padding: theme.spacing(2, 4, 3),
    },
    progress: {
        margin: 10,
    },
    otherMicIcon: {
        backgroundColor: '#2CB174',
        color: '#fff',
        borderRadius: '50%',
        margin: '0px 1px',
        padding: '2px',
        fontSize: 15,
        [theme.breakpoints.between(1280, 1500)]: {
            fontSize: 13,
        },
        [theme.breakpoints.between(1000, 1300)]: {
            fontSize: 11,
        },
    },
    agentMicIcon: {
        backgroundColor: '#eb6d20',
        color: '#fff',
        borderRadius: '50%',
        margin: '0px 1px',
        padding: '2px',
        fontSize: 15,
        [theme.breakpoints.between(1280, 1500)]: {
            fontSize: 13,
        },
        [theme.breakpoints.between(1000, 1300)]: {
            fontSize: 11,
        },
    },
    customerMicIcon: {
        backgroundColor: '#2694D3',
        color: '#fff',
        borderRadius: '50%',
        margin: '0px 1px',
        padding: '2px',
        fontSize: 15,
        [theme.breakpoints.between(1280, 1500)]: {
            fontSize: 13,
        },
        [theme.breakpoints.between(1000, 1300)]: {
            fontSize: 11,
        },
    },
    coachMicIcon: {
        backgroundColor: '#882647',
        color: '#fff',
        borderRadius: '50%',
        margin: '0px 1px',
        padding: 2,
        fontSize: 15,
        [theme.breakpoints.between(1280, 1500)]: {
            fontSize: 13,
        },
        [theme.breakpoints.between(1000, 1300)]: {
            fontSize: 11,
        },
    },
    passFailDiv: {
        fontSize: '1rem',
        lineHeight: 1.5,
        margin: '1rem 0rem',
    },
    iconImport: {
        color: '#3E5785 !important',
        paddingTop: 3,
        cursor: 'pointer',
    },
}));

/**
 * Generates and returns the style object for positioning a modal element.
 *
 * The returned object contains CSS style properties (`top`, `left`, and `transform`)
 * that position the modal at the center of the container with a percentage-based positioning
 * and a translation to ensure proper centering.
 *
 * @returns {Object} An object containing the following style properties:
 * - `top`: A string representing the vertical position as a percentage (e.g., "50%").
 * - `left`: A string representing the horizontal position as a percentage (e.g., "50%").
 * - `transform`: A string defining the translation to center the modal both vertically and horizontally.
 */
export const getModalStyle = () => {
    const top = 50;
    const left = 50;

    return {
        top: `${top}%`,
        left: `${left}%`,
        transform: `translate(-${top}%, -${left}%)`,
    };
};
